import { Alert, Box } from '@mui/material';
import QcPageLayout from '../components/QcPageLayout';
import {
  QC_TASK_REPORT_PAGE_ID,
  QC_TASK_REPORT_TABS,
  useQcTaskReportsPageController,
} from './mt-qc-task-report-001';
import {
  QcTaskAssignmentsSummary,
  QcTaskTable,
  QcTaskWorkflowBatchFilters,
} from './mt-qc-task-001';

const REPORT_TOOLBAR_TABS = [
  { value: QC_TASK_REPORT_TABS.APPROVED, label: 'Approved' },
  { value: QC_TASK_REPORT_TABS.REJECTED, label: 'Rejected' },
];

/**
 * MT-QC-TASK-REPORT-001 — QC Task Reports: same table/filters as My QC Task,
 * with Approved / Rejected tabs (`req_status` APPROVED / REJECTED), batch total beside the table summary line,
 * and read-only assessment modal.
 */
export default function MyQcTaskReportsPage() {
  const {
    activeTab,
    workflowId,
    batchId,
    workflowValue,
    batchValue,
    workflowOptions,
    batchOptions,
    handleTabChange,
    handleWorkflowChange,
    handleBatchChange,
    isWorkflowLoading,
    isWorkflowFetching,
    isBatchesLoading,
    isBatchesFetching,
    rows,
    rowCount,
    tableLoading,
    tableFilters,
    sorting,
    onSortingChange,
    pagination,
    setPagination,
    refetchAssignments,
    pageSizeOptions,
    defaultPageSize,
    errorMessage,
    applyTableFiltersToUrl,
  } = useQcTaskReportsPageController();

  const isRejectedTab = activeTab === QC_TASK_REPORT_TABS.REJECTED;
  const totalLabel = isRejectedTab ? 'Total Rejected' : 'Total Approved';
  const shortLabel = isRejectedTab ? 'Rejected' : 'Approved';

  /** Batch total — same row as “Showing x of y” above the table. */
  const summaryEndTotal = (
    <output
      className={
        isRejectedTab
          ? 'inline-flex max-w-full items-center gap-1.5 text-red-800 sm:gap-2 dark:text-red-200'
          : 'inline-flex max-w-full items-center gap-1.5 text-emerald-900 sm:gap-2 dark:text-emerald-200'
      }
      aria-live="polite"
      title={totalLabel}
      aria-label={`${totalLabel}${tableLoading ? ', loading' : `: ${rowCount}`}`}
    >
      <span
        className={
          isRejectedTab
            ? 'flex shrink-0 items-center text-red-600 dark:text-red-400'
            : 'flex shrink-0 items-center text-emerald-600 dark:text-emerald-400'
        }
        aria-hidden
      >
        {isRejectedTab ? (
          <svg
            className="h-3 w-3"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.25}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        ) : (
          <svg
            className="h-3 w-3"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.25}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M5 13l4 4L19 7"
            />
          </svg>
        )}
      </span>
      <span className="flex min-w-0 items-center gap-1.5 leading-none sm:gap-2">
        <span
          className={
            isRejectedTab
              ? 'shrink-0 whitespace-nowrap text-[10px] font-semibold uppercase tracking-wide text-red-800/95 dark:text-red-200/95 sm:text-[11px]'
              : 'shrink-0 whitespace-nowrap text-[10px] font-semibold uppercase tracking-wide text-emerald-900/95 dark:text-emerald-200/95 sm:text-[11px]'
          }
        >
          {shortLabel}
        </span>
        <span
          className={
            isRejectedTab
              ? 'h-3 w-px shrink-0 bg-red-600/25 dark:bg-red-300/30'
              : 'h-3 w-px shrink-0 bg-emerald-700/25 dark:bg-emerald-300/30'
          }
          aria-hidden
        />
        <span
          className={
            isRejectedTab
              ? 'min-w-[1.1rem] font-mono text-xs font-bold tabular-nums tracking-tight text-red-700 dark:text-red-100 sm:text-sm'
              : 'min-w-[1.1rem] font-mono text-xs font-bold tabular-nums tracking-tight text-emerald-800 dark:text-emerald-50 sm:text-sm'
          }
        >
          {tableLoading ? (
            <span
              className={
                isRejectedTab
                  ? 'inline-block h-3.5 w-7 animate-pulse rounded-sm bg-red-600/20 align-middle sm:h-4 sm:w-8'
                  : 'inline-block h-3.5 w-7 animate-pulse rounded-sm bg-emerald-700/20 align-middle sm:h-4 sm:w-8'
              }
            />
          ) : (
            rowCount.toLocaleString()
          )}
        </span>
      </span>
    </output>
  );

  return (
    <QcPageLayout
      title="QC Task Reports"
      metaDescription="Approved and rejected QC tasks for the selected batch."
      breadcrumbTitle="QC Task Reports"
      breadcrumbSubtitle={`Page ID: ${QC_TASK_REPORT_PAGE_ID}`}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 0.5, sm: 1, md: 1.5, lg: 2, xl: 2.5 },
          pb: { xs: 'max(4px, env(safe-area-inset-bottom, 0px))', sm: 0 },
        }}
      >
        <QcTaskWorkflowBatchFilters
          activeTab={activeTab}
          workflowId={workflowId}
          workflowValue={workflowValue}
          batchValue={batchValue}
          workflowOptions={workflowOptions}
          batchOptions={batchOptions}
          isWorkflowLoading={isWorkflowLoading}
          isWorkflowFetching={isWorkflowFetching}
          isBatchesLoading={isBatchesLoading}
          isBatchesFetching={isBatchesFetching}
          onWorkflowChange={handleWorkflowChange}
          onBatchChange={handleBatchChange}
        />

        {errorMessage ? (
          <Alert
            severity="error"
            sx={{
              py: { xs: 0.35, sm: 0.75 },
              px: { xs: 1, sm: 1.5 },
              fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
              '& .MuiAlert-icon': { fontSize: { xs: '1.1rem', sm: '1.25rem' } },
            }}
          >
            {errorMessage}
          </Alert>
        ) : null}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 0, sm: 0.125, md: 0.25 },
          }}
        >
          <QcTaskAssignmentsSummary
            rowsLength={rows.length}
            rowCount={rowCount}
            loading={tableLoading}
            endAdornment={summaryEndTotal}
          />

          <QcTaskTable
            key={`qc-reports-table-${activeTab}-${workflowId}-${batchId}`}
            data={rows}
            pageWorkflowId={workflowId}
            pageSizeOptions={pageSizeOptions}
            defaultPageSize={defaultPageSize}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            pagination={pagination}
            onPaginationChange={setPagination}
            onRefreshTable={refetchAssignments}
            rowCount={rowCount}
            isLoading={tableLoading}
            toolbarTabItems={REPORT_TOOLBAR_TABS}
            assessmentForceReadOnly
            tableFilters={tableFilters}
            onTableFiltersChange={applyTableFiltersToUrl}
            sorting={sorting}
            onSortingChange={onSortingChange}
          />
        </Box>
      </Box>
    </QcPageLayout>
  );
}
